import './WorkspaceSettings.scss';

import * as React from 'react';

import {
  SlCopyButton,
  SlDropdown,
  SlMenu,
} from '@shoelace-style/shoelace/dist/react';
import { PlanType } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_WORKSPACE_SETTINGS,
  Workspace,
  WorkspaceMemberRoleFloor,
  WorkspaceSettings as WorkspaceSettingsContract,
  isWorkspaceMemberRoleFloor,
} from '~api/workspaces/types';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { SmallButton } from '~shared/components/buttons';
import { Divider } from '~shared/components/Divider';
import { IconChevron } from '~shared/components/icons/micro';
import {
  IconCheckmark,
  IconCopy2,
  IconX,
} from '~shared/components/icons/standard';
import TextInput from '~shared/components/TextInput';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TeamSettingMenuItem } from '~teacher/components/Account/TeamSettings/components';
import { Setting } from '~teacher/components/Account/TeamSettings/components/Setting';
import { DeleteWorkspaceDialog } from '~teacher/components/workspaces/DeleteWorkspaceDialog/DeleteWorkspaceDialog';
import { useDebounce } from '~teacher/hook/useDebounceCallback';
import {
  useGetWorkspaceDetail,
  useWorkspacePermissionsV2,
} from '~teacher/hook/workspaces';
import { useGetWorkspaceSettings } from '~teacher/hook/workspaces/useGetWorkspaceSettings';
import { useRenameWorkspace } from '~teacher/hook/workspaces/useRenameWorkspace';
import { useUpdateWorkspaceSettings } from '~teacher/hook/workspaces/useUpdateWorkspaceSettings';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import { LeaveLibraryDialog } from './components/LeaveLibraryDialog';
import { LibrarySettingsPaywall } from './components/LibrarySettingsPaywall';

interface SettingsProps {
  workspaceId: string;
}

type BoolKey = {
  [K in keyof WorkspaceSettingsContract]: WorkspaceSettingsContract[K] extends boolean
    ? K
    : never;
}[keyof WorkspaceSettingsContract];

// Stable reference so React Query can memoize the selected result (an inline
// select returns a fresh object every render and defeats structural sharing).
// `plan` falls back to Free while the query is in-flight to avoid flashing
// unlocked controls before the answer arrives.
const selectWorkspaceGate = (workspace: Workspace) => ({
  plan: workspace.plan ?? PlanType.Free,
  isAssessmentsLibrary: !!workspace.isAssessmentsLibrary,
  insightsEnabled: !!workspace.insightsEnabled,
});

/**
 * Library settings panel. Mirrors the grid + subgrid layout used by
 * `AccountPage.tsx` (the Billing tab):
 *
 *  - Outer container is `library-settings-grid` (`1fr min-content` on md+,
 *    flex column on mobile — matches `account-page-grid`).
 *  - Each section wraps its rows in `<Section>` which sets
 *    `grid grid-cols-subgrid col-span-2 gap-2`. Children of `<Setting>` then
 *    drop naturally into `[name+description, control]`.
 *  - Section headings and `<Divider />` span both columns via `col-span-2`.
 */
export function WorkspaceSettings({ workspaceId }: SettingsProps) {
  const {
    permissions: {
      deleteWorkspaceForever: canDeleteWorkspaceForever,
      editWorkspaceSettings,
      renameWorkspace: canRenameWorkspace,
    },
  } = useWorkspacePermissionsV2({ workspaceId });

  // Gate every setting control on the dedicated `editWorkspaceSettings`
  // permission so the widening to Admin (SOC-5450) lights up automatically
  // when the `admin` column in `workspaceUserPermissions.json` is filled.
  // `canRenameWorkspace` and `canDeleteWorkspaceForever` keep their own
  // permission ops because spec reserves those library-destructive actions
  // for Owner-only (Admin can edit settings but can't delete/rename).
  // While `useWorkspacePermissionsV2` is still loading, all flags read
  // `undefined` → `canEditSettings` is `false`, preventing flicker on mount.
  const canEditSettings = !!editWorkspaceSettings;

  // Key Org gating off the *workspace owner's* plan, not the requesting user's
  // account plan — any active member of an Org-owned library sees ORG-tier
  // behavior even if they're personally on Free/Pro. Resolved on demand by the
  // workspace-details endpoint, alongside the assessments/insights flags below.
  const gate = useGetWorkspaceDetail<{
    plan: PlanType;
    isAssessmentsLibrary: boolean;
    insightsEnabled: boolean;
  }>({
    workspaceId,
    options: { select: selectWorkspaceGate },
  }).data;
  const isOrgPlan = gate?.plan === PlanType.Organization;
  const isAssessments = !!gate?.isAssessmentsLibrary;
  const insightsEnabled = !!gate?.insightsEnabled;

  // Whether the B2B settings are gated behind a purchase the viewer lacks:
  //   - Assessments libraries → the Insights add-on.
  //   - Native Socrative → the Organization plan.
  // Gated settings always render (the panel is never hidden) but show a badge.
  // How the barrier is enforced differs by app — see `isControlDisabled` and
  // `gateSettingClick`.
  const isGated = isAssessments ? !insightsEnabled : !isOrgPlan;

  // Badge on the gated section headers (Content restrictions, Member
  // permissions): "ADD-ON" in Assessments (Insights add-on), "ORG" in native
  // Socrative.
  const planGateBadge = isGated ? (
    <Badge tone="pro">{isAssessments ? 'ADD-ON' : 'ORG'}</Badge>
  ) : null;

  // Only assessments disables gated controls (no click-through paywall — the
  // greyed control + ADD-ON badge is the barrier). Native keeps controls
  // enabled so a click opens the paywall. Non-edit roles never reach here — the
  // whole panel is gated on `canEditSettings` below.
  const isControlDisabled = isAssessments && isGated;

  const settingsQuery = useGetWorkspaceSettings({ workspaceId });
  const settings = settingsQuery.data ?? DEFAULT_WORKSPACE_SETTINGS;

  const updateSettings = useUpdateWorkspaceSettings({ workspaceId });
  const queryClient = useQueryClient();

  const [showPaywall, setShowPaywall] = React.useState(false);
  const openPaywall = React.useCallback(() => setShowPaywall(true), []);

  /**
   * Debounced commit pattern (matches `useDebounce` in `useDebounceCallback.ts`):
   *  - `queueSettingsUpdate` updates the React Query cache synchronously so
   *    the toggle/dropdown flips visually on the same tick the user clicked
   *    (no network round-trip lag).
   *  - The actual network mutation is collapsed into a single PUT after a
   *    short idle window, regardless of how many fields the user touched in
   *    the burst. Rapid flipping (e.g. on→off→on) sends ONE request with the
   *    final merged delta instead of one per click.
   *  - All accumulated deltas live in `pendingPatchRef`; the ref is drained
   *    when the debounced callback fires.
   */
  const pendingPatchRef = React.useRef<Partial<WorkspaceSettingsContract>>({});
  // Snapshot of the last server-confirmed settings, taken at the START of a
  // debounce burst (before the first optimistic write). Rollback target if the
  // collapsed PUT fails — capturing it inside the mutation's `onMutate` would
  // be too late, since the cache is already optimistically flipped by then.
  const rollbackRef = React.useRef<WorkspaceSettingsContract | null>(null);

  const commitPendingUpdate = React.useCallback(() => {
    const patch = pendingPatchRef.current;
    if (Object.keys(patch).length === 0) return;
    pendingPatchRef.current = {};
    const rollback = rollbackRef.current;
    rollbackRef.current = null;
    updateSettings.mutate(patch, {
      onError: () => {
        // Restore the pre-burst snapshot so a failed save doesn't leave the
        // control stuck in its optimistic position until the slower
        // `onSettled` refetch lands. The hook still surfaces the error toast.
        if (rollback) {
          queryClient.setQueryData(
            workspaceKeys.settings(workspaceId),
            rollback
          );
        }
      },
    });
  }, [updateSettings, queryClient, workspaceId]);

  const flushSettingsUpdate = useDebounce({
    delay: 300,
    callback: commitPendingUpdate,
  });

  const queueSettingsUpdate = React.useCallback(
    (patch: Partial<WorkspaceSettingsContract>) => {
      // Capture the rollback snapshot once per burst, before the first
      // optimistic write mutates the cache below.
      if (Object.keys(pendingPatchRef.current).length === 0) {
        rollbackRef.current =
          queryClient.getQueryData<WorkspaceSettingsContract>(
            workspaceKeys.settings(workspaceId)
          ) ?? null;
      }
      // Immediate optimistic UI: the toggle/dropdown flips on the same tick.
      // The actual PUT is collapsed into one request after the debounce idle.
      queryClient.setQueryData<WorkspaceSettingsContract>(
        workspaceKeys.settings(workspaceId),
        (prev) => ({ ...(prev ?? DEFAULT_WORKSPACE_SETTINGS), ...patch })
      );
      pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
      flushSettingsUpdate();
    },
    [queryClient, workspaceId, flushSettingsUpdate]
  );

  // Flush any pending debounced change on unmount so a control toggled right
  // before navigating away still persists, then cancel the timer so it can't
  // fire again against the unmounted component. The pending-ref drain in
  // `commitPendingUpdate` keeps the flush + any already-queued timer
  // idempotent. `commitRef` pins the cleanup to a single mount/unmount cycle
  // even as `commitPendingUpdate`'s identity changes between renders.
  const commitRef = React.useRef(commitPendingUpdate);
  React.useLayoutEffect(() => {
    commitRef.current = commitPendingUpdate;
  });
  React.useEffect(
    () => () => {
      commitRef.current();
      flushSettingsUpdate.cancel();
    },
    [flushSettingsUpdate]
  );

  /**
   * Wraps a control's `onChange` with the entitlement gate. Only reached from
   * inside the settings panel, which is already gated on `canEditSettings`.
   *  - Assessments: no paywall modal (buying the add-on isn't a self-serve
   *    flow). When gated the control is disabled, so the `isGated` guard only
   *    drops the affirming `onChange` a disabled toggle can still emit.
   *  - Native Socrative: non-Org opens the paywall and the change is dropped;
   *    Org runs the handler.
   */
  const gateSettingClick = React.useCallback(
    <Args extends unknown[]>(handler: (...args: Args) => void) => {
      return (...args: Args) => {
        if (isAssessments) {
          if (isGated) return;
          handler(...args);
          return;
        }
        if (!isOrgPlan) {
          openPaywall();
          return;
        }
        handler(...args);
      };
    },
    [isAssessments, isGated, isOrgPlan, openPaywall]
  );

  const setBool = React.useCallback(
    (key: BoolKey, next: boolean) => {
      queueSettingsUpdate({
        [key]: next,
      } as Partial<WorkspaceSettingsContract>);
    },
    [queueSettingsUpdate]
  );

  const setMemberInvite = React.useCallback(
    (next: WorkspaceMemberRoleFloor) => {
      queueSettingsUpdate({ allowMemberInvite: next });
    },
    [queueSettingsUpdate]
  );

  // "Official Library" is a boolean view over the tri-state `readOnlyMode`:
  // ON locks editing for everyone (`'everyone'`); Admins/Owners unlock by
  // toggling it back to `'off'`. The `'admin_only'` value isn't reachable from
  // this toggle (no UI for the editor-only lock today).
  const isOfficialLibrary = settings.readOnlyMode !== 'off';
  const setOfficialLibrary = React.useCallback(
    (next: boolean) => {
      queueSettingsUpdate({ readOnlyMode: next ? 'everyone' : 'off' });
    },
    [queueSettingsUpdate]
  );

  return (
    <div className="library-settings-grid mt-4">
      {/* General / Content / Member sections are only meaningful to roles that
          can edit settings (Owners + Admins). Editors and Viewers see just the
          Danger Zone below, with a Leave action instead of Delete. */}
      {canEditSettings ? (
        <>
          <SectionDivider />

          <Section heading={translate('General')}>
            <RenameLibraryRow
              workspaceId={workspaceId}
              canRename={canRenameWorkspace}
            />
          </Section>

          <SectionDivider />

          <Section
            heading={
              <span className="inline-flex items-center gap-2">
                {translate('Content restrictions')}
                {planGateBadge}
              </span>
            }
          >
            <Setting
              name={
                <Setting.Name>
                  {translate('Copy to personal library')}
                </Setting.Name>
              }
              description={null}
            >
              <Setting.Control>
                <ToggleSwitch
                  size="small"
                  on={settings.allowCopyToPersonal}
                  disabled={isControlDisabled}
                  onChange={gateSettingClick((next: boolean) =>
                    setBool('allowCopyToPersonal', next)
                  )}
                  label={translate('Copy to personal library')}
                />
              </Setting.Control>
            </Setting>

            <Setting
              name={
                <Setting.Name>
                  {translate('Copy to another library')}
                </Setting.Name>
              }
              description={null}
            >
              <Setting.Control>
                <ToggleSwitch
                  size="small"
                  on={settings.allowCopyToLibrary}
                  disabled={isControlDisabled}
                  onChange={gateSettingClick((next: boolean) =>
                    setBool('allowCopyToLibrary', next)
                  )}
                  label={translate('Copy to another library')}
                />
              </Setting.Control>
            </Setting>

            {/* Merged row: one toggle drives `allowDownloadPrint`, which now
                governs both quiz download and answer-key export. */}
            <Setting
              name={
                <Setting.Name>
                  {translate('Download quiz & answer key')}
                </Setting.Name>
              }
              description={null}
            >
              <Setting.Control>
                <ToggleSwitch
                  size="small"
                  on={settings.allowDownloadPrint}
                  disabled={isControlDisabled}
                  onChange={gateSettingClick((next: boolean) =>
                    setBool('allowDownloadPrint', next)
                  )}
                  label={translate('Download quiz & answer key')}
                />
              </Setting.Control>
            </Setting>

            <Setting
              name={<Setting.Name>{translate('Share quiz')}</Setting.Name>}
              description={null}
            >
              <Setting.Control>
                <ToggleSwitch
                  size="small"
                  on={settings.allowSocCode}
                  disabled={isControlDisabled}
                  onChange={gateSettingClick((next: boolean) =>
                    setBool('allowSocCode', next)
                  )}
                  label={translate('Share quiz')}
                />
              </Setting.Control>
            </Setting>

            <Setting
              name={
                <Setting.Name>{translate('Official Library')}</Setting.Name>
              }
              description={
                <Setting.Description>
                  {translate(
                    'Mark this library as your organization’s approved content, visible to teachers when browsing and launching. Editing is locked to keep content consistent across your organization.'
                  )}
                </Setting.Description>
              }
            >
              <Setting.Control>
                <ToggleSwitch
                  size="small"
                  on={isOfficialLibrary}
                  disabled={isControlDisabled}
                  onChange={gateSettingClick((next: boolean) =>
                    setOfficialLibrary(next)
                  )}
                  label={translate('Official Library')}
                />
              </Setting.Control>
            </Setting>
          </Section>

          <SectionDivider />

          <Section
            heading={
              <span className="inline-flex items-center gap-2">
                {translate('Member permissions')}
                {planGateBadge}
              </span>
            }
            gap="gap-4"
          >
            <Setting
              name={
                <Setting.Name>
                  {translate('Who can share invite links')}
                </Setting.Name>
              }
              description={null}
            >
              <Setting.Control>
                <MemberRoleFloorDropdown
                  value={settings.allowMemberInvite}
                  isDisabled={isControlDisabled}
                  onSelect={gateSettingClick(setMemberInvite)}
                  floors={['viewer', 'editor', 'admin']}
                />
              </Setting.Control>
            </Setting>

            <Setting
              name={
                <Setting.Name>
                  {translate('Hide member list from Viewers and Guests')}
                </Setting.Name>
              }
              description={null}
            >
              <Setting.Control>
                <ToggleSwitch
                  size="small"
                  on={settings.hideMemberList}
                  disabled={isControlDisabled}
                  onChange={gateSettingClick((next: boolean) =>
                    setBool('hideMemberList', next)
                  )}
                  label={translate('Hide member list from Viewers and Guests')}
                />
              </Setting.Control>
            </Setting>
          </Section>
        </>
      ) : null}

      <SectionDivider />

      <Section heading={translate('Danger zone')}>
        {/* Owners get the destructive Delete action; everyone else (Admins
            without delete rights, Editors, Viewers) gets Leave instead — per
            "show the leave action when the role doesn't see Delete". */}
        {canDeleteWorkspaceForever ? (
          <DeleteLibraryRow workspaceId={workspaceId} canDelete />
        ) : (
          <LeaveLibraryRow workspaceId={workspaceId} />
        )}
      </Section>

      <SectionDivider />

      <LibrarySettingsPaywall
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    </div>
  );
}

interface SectionProps {
  heading: React.ReactNode;
  children: React.ReactNode;
  /**
   * Tailwind row-gap class. Defaults to `gap-8` (matches Content restrictions).
   * Pass a smaller value (e.g. `gap-4`) for sections whose row controls are
   * naturally taller — e.g. dropdown rows in Member permissions — so the
   * perceived vertical rhythm matches sections of short toggle rows.
   */
  gap?: string;
}

/**
 * One section of the panel. Spans both grid columns and re-establishes
 * `grid-cols-subgrid` so each `<Setting>` child's two siblings
 * (`name+description`, `control`) drop into the inherited `[1fr min-content]`
 * tracks. Mirrors `TeamSubscriptionContainer` from the Billing tab.
 */
function Section({ heading, children, gap = 'gap-8' }: SectionProps) {
  // Per-row gap + `pb-4` after the last row so the trailing `<Divider />`
  // doesn't sit flush against the last setting; combined with the parent
  // grid's gap-4 it lands at ~32px of breathing room.
  return (
    <div
      className={`grid ${gap} pb-4 col-span-2 grid-cols-subgrid items-center`}
    >
      <div className="col-span-2">
        <span className="font-display font-700 text-display leading-8 text-blue-900">
          {heading}
        </span>
      </div>
      {children}
    </div>
  );
}

/** Full-width horizontal rule between sections; mirrors AccountPage. */
function SectionDivider() {
  return (
    <div className="col-span-2">
      <Divider />
    </div>
  );
}

interface RenameLibraryRowProps {
  workspaceId: string;
  canRename: boolean;
}

/**
 * Drops cleanly into the section's subgrid: label spans both columns on row 1,
 * input takes the 1fr column on row 2, Update button takes the min-content
 * column. No bespoke flex/width plumbing needed.
 */
function RenameLibraryRow({ workspaceId, canRename }: RenameLibraryRowProps) {
  const workspaceInfo = useGetWorkspaceDetail({ workspaceId });
  const [newName, setNewName] = React.useState('');

  React.useEffect(
    () => setNewName(workspaceInfo.data?.name ?? ''),
    [workspaceInfo.data?.name]
  );

  // No reset-on-success callback: clearing the field here would blank the
  // input until the post-rename refetch refilled it from server data, causing
  // a visible flash. Leaving `newName` as-is keeps the just-saved value shown;
  // the effect above re-syncs it (to the same value) once the refetch lands.
  const { rename } = useRenameWorkspace(() => undefined);

  const handleSave = () => rename.mutate({ id: workspaceId, name: newName });

  const trimmedName = newName.trim();
  const savedName = (workspaceInfo.data?.name ?? '').trim();
  // Disable Update unless there's a real, non-empty change to persist.
  const isUpdateDisabled =
    !canRename || trimmedName === '' || trimmedName === savedName;

  // Wrap label + input + button in a single `col-span-2` block so the
  // section's `gap-8` rhythm doesn't apply between them — they need a much
  // tighter `gap-2` (label) and inline button row.
  return (
    <div className="col-span-2 flex flex-col gap-2">
      <label
        htmlFor="library-name"
        className="text-base font-sans font-400 text-blue-900 leading-8"
      >
        {translate('Library name')}
      </label>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <TextInput
            id="library-name"
            readOnly={!canRename}
            disabled={!canRename}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={translate('Library name')}
          />
        </div>
        <SmallButton
          appearance="default"
          isDisabled={isUpdateDisabled}
          onClick={handleSave}
        >
          {translate('Update')}
        </SmallButton>
      </div>
      {/* Read-only library ID caption (design option 1b): mirrors the Team ID
          caption and adds a copy button so admins can match this library to
          the admin-dashboard Libraries list. */}
      <div className="mt-1 flex items-center gap-1.5 font-sans text-xs leading-tight text-grey-900">
        <span>
          {translate('Library ID')}: {workspaceId}
        </span>
        <SlCopyButton
          data-testid="copy-library-id"
          className="library-id-copy"
          value={workspaceId}
          copy-label={translate('Copy library ID')}
          success-label={translate('Copied')}
          error-label={translate('Error')}
        >
          <div slot="copy-icon" className="flex text-grey-700">
            <IconCopy2 size="micro" />
          </div>
          <div slot="success-icon" className="flex text-green-700">
            <IconCheckmark size="micro" />
          </div>
          <div slot="error-icon" className="flex">
            <IconX size="micro" />
          </div>
        </SlCopyButton>
      </div>
    </div>
  );
}

interface DeleteLibraryRowProps {
  workspaceId: string;
  canDelete: boolean;
}

function DeleteLibraryRow({ workspaceId, canDelete }: DeleteLibraryRowProps) {
  const [showDialog, setShowDialog] = React.useState(false);
  return (
    <>
      <Setting
        name={<Setting.Name>{translate('Delete library')}</Setting.Name>}
        description={
          <Setting.Description>
            {translate(
              'Deleting the library will irreversibly delete all its quizzes and disband members.'
            )}
          </Setting.Description>
        }
      >
        <Setting.Control>
          <SmallButton
            appearance="error"
            isDisabled={!canDelete}
            onClick={() => setShowDialog(true)}
          >
            <span className="font-600 whitespace-no-wrap">
              {translate('Delete Library Forever')}
            </span>
          </SmallButton>
        </Setting.Control>
      </Setting>
      <DeleteWorkspaceDialog
        workspaceId={workspaceId}
        isOpen={showDialog}
        setIsOpen={setShowDialog}
      />
    </>
  );
}

interface LeaveLibraryRowProps {
  workspaceId: string;
}

/**
 * Danger-zone row shown to roles that can't delete the library (Admins without
 * delete rights, Editors, Viewers). Leaving reuses the existing self-removal
 * flow via {@link LeaveLibraryDialog}.
 */
function LeaveLibraryRow({ workspaceId }: LeaveLibraryRowProps) {
  const [showDialog, setShowDialog] = React.useState(false);
  return (
    <>
      <Setting
        name={<Setting.Name>{translate('Leave library')}</Setting.Name>}
        description={
          <Setting.Description>
            {translate(
              'You will lose access to the library and its quiz reports.'
            )}
          </Setting.Description>
        }
      >
        <Setting.Control>
          <SmallButton appearance="risky" onClick={() => setShowDialog(true)}>
            <span className="font-600 whitespace-no-wrap">
              {translate('Leave library')}
            </span>
          </SmallButton>
        </Setting.Control>
      </Setting>
      <LeaveLibraryDialog
        workspaceId={workspaceId}
        isOpen={showDialog}
        setIsOpen={setShowDialog}
      />
    </>
  );
}

interface MemberRoleFloorDropdownProps {
  value: WorkspaceMemberRoleFloor;
  isDisabled: boolean;
  onSelect: (next: WorkspaceMemberRoleFloor) => void;
  // Ordered list of floors to offer. Differs per setting: "Who can share
  // invite links" includes 'viewer' (Viewers can share with other Viewers); a
  // quiz-editing floor would start at 'editor' since Viewers can't edit.
  floors: WorkspaceMemberRoleFloor[];
}

/**
 * Role-floor dropdown used by member-permission settings (e.g. "Who can share
 * invite links"). The caller supplies the `floors` it offers.
 *
 * The stored value is the *minimum* role required to perform the gated
 * action (see `WorkspaceMemberRoleFloor` in `api/workspaces/types.ts`).
 *  - `'viewer'` → "Viewers & above" (everyone qualifies)
 *  - `'editor'` → "Editors & above" (Editors, Admins, Owners qualify)
 *  - `'admin'`  → "Admins only"     (Admins, Owners qualify)
 *  - `'owner'`  → "Owners only"     (reserved; no UI today)
 *
 * The inner `whitespace-no-wrap` wrapper prevents menu items from collapsing
 * to a single word in narrow panel widths (the shoelace SlMenu otherwise
 * inherits the trigger's `min-content` column width on the parent grid).
 */
function MemberRoleFloorDropdown({
  value,
  isDisabled,
  onSelect,
  floors,
}: MemberRoleFloorDropdownProps) {
  // Static `translate()` calls (not a lookup map) so l10n extraction sees each
  // literal string.
  const labelFor = (v: WorkspaceMemberRoleFloor): string => {
    switch (v) {
      case 'viewer':
        return translate('Viewers & above');
      case 'admin':
        return translate('Admins only');
      case 'owner':
        return translate('Owners only');
      case 'editor':
      default:
        return translate('Editors & above');
    }
  };

  return (
    <SlDropdown>
      <div slot="trigger" className="grid">
        <SmallButton
          isDisabled={isDisabled}
          appearance="grey-outline"
          suffix={<IconChevron />}
        >
          <span className="font-600 whitespace-no-wrap">{labelFor(value)}</span>
        </SmallButton>
      </div>
      {/* Inline `minWidth` on the SlMenu — the shadow-DOM ::part(base) on
          each sl-menu-item is `overflow:hidden` with a `1fr` content column,
          so any host-level min-width via SCSS gets clipped before it can
          push the panel wider. Forcing the menu itself to a generous min
          width is the only reliable way to give the slotted content room. */}
      <SlMenu
        style={{ minWidth: '14rem' }}
        onSlSelect={(evt: Event) => {
          evt.preventDefault();
          // `detail.item.value` is untyped DOM data; validate before
          // persisting so a future menu-item change can't silently PUT an
          // invalid floor (the BE would 400, but fail fast on the client).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const value = (evt as any).detail?.item?.value;
          if (isWorkspaceMemberRoleFloor(value)) {
            onSelect(value);
          }
        }}
      >
        {/* Skip `TeamSettingMenuItem.Content` here. Its outer
            `min-w-40 max-w-92 w-fit` + inner `overflow-ellipsis` combine
            with the host part(base)'s clip to truncate "Editors & above"
            even when the panel is wide enough. Render a plain padded div
            with `whitespace-nowrap` so the text always renders in full. */}
        {floors.map((floor) => (
          <TeamSettingMenuItem key={floor} value={floor}>
            <div className="font-sans leading-8 text-base font-400 text-blue-900 whitespace-no-wrap">
              {labelFor(floor)}
            </div>
          </TeamSettingMenuItem>
        ))}
      </SlMenu>
    </SlDropdown>
  );
}
